import { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input, NativeSelect } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Field, FieldGrid } from "@/components/shared/form";
import { blockNumberWheel } from "@/components/shared/line-grid";
import { TUBE_ORDER_SHAPES, formatShapeSizeLabel } from "@/features/materials/constants";
import { useProcessTypes } from "@/features/machines/api";
import { useMaterialGrades, useShapeSizes } from "@/features/materials/api";
import { remainingWeight, type QualityStatus, type ReceiveLineInput } from "@/features/purchase/receive-api";
import { calcWtPerMm, receiveFieldRules } from "@/features/purchase/receive-fields";
import type { PurchaseOrderLine } from "@/features/purchase/types";

const QUALITY: { value: QualityStatus; label: string }[] = [
  { value: "pending", label: "Pending" },
  { value: "hold", label: "Hold" },
  { value: "passed", label: "Passed" },
  { value: "rejected", label: "Rejected" },
];

export type ReceiveLineDialogDefaults = {
  location_id: string;
  material_grade_id: string;
  shape_size_id: string;
  quantity: string;
  weight: string;
};

type Props = {
  open: boolean;
  line: PurchaseOrderLine | null;
  locationOptions: { id: string; label: string }[];
  defaults: ReceiveLineDialogDefaults;
  /** Prefill from a previously staged receive line */
  staged?: ReceiveLineInput | null;
  busy?: boolean;
  onClose: () => void;
  onConfirm: (input: ReceiveLineInput) => void | Promise<void>;
};

export function ReceiveLineDialog({
  open,
  line,
  locationOptions,
  defaults,
  staged,
  busy,
  onClose,
  onConfirm,
}: Props) {
  const { data: grades = [] } = useMaterialGrades();
  const { data: processTypes = [] } = useProcessTypes();
  const rules = line ? receiveFieldRules(line) : null;
  const { data: allSizes = [] } = useShapeSizes(rules?.shapeMaterialId);

  const [materialGradeId, setMaterialGradeId] = useState("");
  const [quantity, setQuantity] = useState("");
  const [weight, setWeight] = useState("");
  const [thickness, setThickness] = useState("");
  const [length, setLength] = useState("");
  const [width, setWidth] = useState("");
  const [wtPerMm, setWtPerMm] = useState("");
  const [shape, setShape] = useState("");
  const [shapeSizeId, setShapeSizeId] = useState("");
  const [locationId, setLocationId] = useState("");
  const [quality, setQuality] = useState<QualityStatus>("pending");
  const [lastProcessTypeId, setLastProcessTypeId] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !line) return;
    setMaterialGradeId(
      staged?.material_grade_id || defaults.material_grade_id || line.material_grade_id || "",
    );
    setQuantity(
      staged
        ? String(staged.quantity)
        : defaults.quantity || (line.qty ? String(line.qty) : "1"),
    );
    setWeight(
      staged
        ? String(staged.weight)
        : defaults.weight || (remainingWeight(line) > 0 ? String(remainingWeight(line)) : ""),
    );
    setThickness(
      staged?.thickness != null
        ? String(staged.thickness)
        : line.thickness != null
          ? String(line.thickness)
          : "",
    );
    setLength(
      staged?.length != null
        ? String(staged.length)
        : line.length != null
          ? String(line.length)
          : "",
    );
    setWidth(
      staged?.width != null
        ? String(staged.width)
        : line.width != null
          ? String(line.width)
          : "",
    );
    setWtPerMm(staged?.wt_per_m != null ? String(staged.wt_per_m) : "");
    setShape("");
    setShapeSizeId(staged?.shape_size_id || defaults.shape_size_id || line.shape_size_id || "");
    setLocationId(staged?.location_id || defaults.location_id || "");
    setQuality(staged?.quality_status || "pending");
    setLastProcessTypeId(
      staged?.last_process_type_id || line.inbound_process_type_id || "",
    );
    setNotes(staged?.notes || "");
    setError(null);
  }, [open, line, defaults, staged]);

  // When sizes load and we have a preselected size, sync shape filter
  useEffect(() => {
    if (!shapeSizeId || shape) return;
    const match = allSizes.find((s) => s.id === shapeSizeId);
    if (match) setShape(match.shape);
  }, [allSizes, shapeSizeId, shape]);

  useEffect(() => {
    const w = Number(weight);
    const wd = Number(width);
    const calc = calcWtPerMm(w, wd);
    setWtPerMm(calc != null ? String(calc) : "");
  }, [weight, width]);

  const sizesForShape = useMemo(
    () => allSizes.filter((s) => s.is_active && (!shape || s.shape === shape)),
    [allSizes, shape],
  );

  if (!line || !rules) return null;

  const rem = remainingWeight(line);

  async function submit() {
    setError(null);
    const qty = Math.max(0, Math.round(Number(quantity) || 0));
    const wt = Number(weight) || 0;
    if (!(wt > 0 || qty > 0)) {
      setError("Enter weight or quantity");
      return;
    }
    if (!materialGradeId) {
      setError("Material grade is required");
      return;
    }
    if (!locationId) {
      setError("Location is required");
      return;
    }
    if (rules!.requireShapeSize && !shapeSizeId) {
      setError("Shape and size are required for this material");
      return;
    }
    const thisWeight = wt > 0 ? wt : qty;
    if (thisWeight > rem + 1e-6) {
      setError(`Cannot over-receive (remaining ${rem})`);
      return;
    }

    await onConfirm({
      purchase_order_line_id: line!.id,
      quantity: qty,
      weight: wt > 0 ? wt : qty,
      location_id: locationId,
      material_grade_id: materialGradeId,
      shape_size_id: rules!.showShapeSize ? shapeSizeId || null : null,
      quality_status: quality,
      notes: notes.trim() || undefined,
      thickness: thickness ? Number(thickness) : null,
      width: rules!.showWidth && width ? Number(width) : null,
      length: rules!.showLength && length ? Number(length) : null,
      wt_per_m: rules!.showWtPerMm && wtPerMm ? Number(wtPerMm) : null,
      last_process_type_id: lastProcessTypeId || null,
    });
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto gap-3">
        <DialogHeader>
          <DialogTitle>Receive — line {line.line_no}</DialogTitle>
          <p className="text-xs text-muted-foreground">
            {line.material_name}
            {line.grade_name ? ` · ${line.grade_name}` : ""}
            {" · Remaining "}
            <span className="tabular-nums font-medium text-foreground">{rem}</span>
            {line.uom_code ? ` ${line.uom_code}` : ""}
          </p>
        </DialogHeader>

        <FieldGrid>
          <Field label="Material" span={6}>
            <Input value={line.material_name ?? ""} disabled readOnly />
          </Field>
          <Field label="Material grade" required span={6}>
            <NativeSelect
              value={materialGradeId}
              onChange={(e) => setMaterialGradeId(e.target.value)}
            >
              <option value="">Select grade…</option>
              {grades
                .filter((g) => g.is_active)
                .map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
            </NativeSelect>
          </Field>

          <Field label="Quantity" required span={3}>
            <Input
              type="number"
              step={1}
              min={0}
              inputMode="numeric"
              className="fs-no-spin"
              onWheel={blockNumberWheel}
              value={quantity}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === "") {
                  setQuantity("");
                  return;
                }
                const n = Number(raw);
                setQuantity(Number.isFinite(n) ? String(Math.max(0, Math.round(n))) : "");
              }}
            />
          </Field>
          <Field label="Weight" required span={3}>
            <Input
              type="number"
              step="any"
              min={0}
              max={rem}
              className="fs-no-spin"
              onWheel={blockNumberWheel}
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
            />
          </Field>
          {rules.showThickness ? (
            <Field label="Thickness" span={3}>
              <Input
                type="number"
                step="any"
                min={0}
                className="fs-no-spin"
                onWheel={blockNumberWheel}
                value={thickness}
                onChange={(e) => setThickness(e.target.value)}
              />
            </Field>
          ) : null}
          {rules.showLength ? (
            <Field label="Length" span={3}>
              <Input
                type="number"
                step="any"
                min={0}
                className="fs-no-spin"
                onWheel={blockNumberWheel}
                value={length}
                onChange={(e) => setLength(e.target.value)}
              />
            </Field>
          ) : null}
          {rules.showWidth ? (
            <Field label="Width" span={3}>
              <Input
                type="number"
                step="any"
                min={0}
                className="fs-no-spin"
                onWheel={blockNumberWheel}
                value={width}
                onChange={(e) => setWidth(e.target.value)}
              />
            </Field>
          ) : null}
          {rules.showWtPerMm ? (
            <Field label="Wt per mm" span={3} hint="(weight ÷ width) × 1000">
              <Input
                type="number"
                step="any"
                className="fs-no-spin"
                value={wtPerMm}
                readOnly
                disabled
              />
            </Field>
          ) : null}

          {rules.showShapeSize ? (
            <>
              <Field
                label="Shape"
                required={rules.requireShapeSize}
                span={6}
              >
                <NativeSelect
                  value={shape}
                  onChange={(e) => {
                    setShape(e.target.value);
                    setShapeSizeId("");
                  }}
                >
                  <option value="">Select shape…</option>
                  {TUBE_ORDER_SHAPES.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </NativeSelect>
              </Field>
              <Field label="Size" required={rules.requireShapeSize} span={6}>
                <NativeSelect
                  value={shapeSizeId}
                  disabled={!shape}
                  onChange={(e) => setShapeSizeId(e.target.value)}
                >
                  <option value="">
                    {shape
                      ? sizesForShape.length
                        ? "Select size…"
                        : "No sizes — add in Materials → Shapes"
                      : "Pick shape first"}
                  </option>
                  {sizesForShape.map((s) => (
                    <option key={s.id} value={s.id}>
                      {formatShapeSizeLabel(s)}
                    </option>
                  ))}
                </NativeSelect>
              </Field>
            </>
          ) : null}

          <Field label="Location" required span={8}>
            <NativeSelect value={locationId} onChange={(e) => setLocationId(e.target.value)}>
              <option value="">Select location…</option>
              {locationOptions.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.label}
                </option>
              ))}
            </NativeSelect>
          </Field>
          <Field label="QC" span={4}>
            <NativeSelect
              value={quality}
              onChange={(e) => setQuality(e.target.value as QualityStatus)}
            >
              {QUALITY.map((q) => (
                <option key={q.value} value={q.value}>
                  {q.label}
                </option>
              ))}
            </NativeSelect>
          </Field>
          <Field
            label="Received as"
            span={12}
            hint="Default: plant pickled (company / PO line). Override for CRM / skin-pass / slit stock."
          >
            <NativeSelect
              value={lastProcessTypeId}
              onChange={(e) => setLastProcessTypeId(e.target.value)}
            >
              <option value="">Default (PICKLING)</option>
              {processTypes.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.code} — {p.name}
                </option>
              ))}
            </NativeSelect>
          </Field>
          <Field label="Notes" span={12}>
            <Textarea
              rows={2}
              className="text-xs"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional"
            />
          </Field>
        </FieldGrid>

        {error ? <p className="text-2xs text-destructive">{error}</p> : null}

        <DialogFooter>
          <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="button" size="sm" disabled={busy} onClick={() => void submit()}>
            {busy ? <Loader2 className="animate-spin" /> : null}
            Add to receipt
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
